/**
 * 슬롯2: 배경·헤더·타이머·룰렛만 캔버스. 내역·검증은 HTML 탭/드롭다운.
 */

import bgSrc from './assets/background.png';
import logoSrc from './assets/logo.png';

export interface CanvasTopModel {
  blockStr: string;
  timerStr: string;
  wheelAngleDeg: number;
  /** HTML 탭 셸 top — 캔버스와 동일 행에 로고 그리기 */
  topInsetPx: number;
  /** HTML 탭 행 높이 — 로고 밴드 세로 */
  logoRowH?: number;
  /** 룰렛 스핀 중 — 중앙 BLOCK/타이머 카드 숨김 */
  blockRowDimmed?: boolean;
}

const SEGMENTS = 16;
/** 세그먼트 중심각 기준, 중심에서 아이콘까지 거리 비율 (클수록 바깥 테두리 쪽) */
const WHEEL_ICON_RADIUS_FR = 0.8;
const WHEEL_RED = 'rgba(180,40,40,0.95)';
const WHEEL_GREEN = 'rgba(30,120,50,0.95)';

const PAD_X = 14;
const MAX_W = 520;

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawWheelRotated(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  rotDeg: number
) {
  const rot = (rotDeg * Math.PI) / 180;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rot);
  ctx.translate(-cx, -cy);

  for (let i = 0; i < SEGMENTS; i++) {
    const a0 = ((i * (360 / SEGMENTS) - 90) * Math.PI) / 180;
    const a1 = (((i + 1) * (360 / SEGMENTS) - 90) * Math.PI) / 180;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, r, a0, a1);
    ctx.closePath();
    ctx.fillStyle = i % 2 === 0 ? WHEEL_RED : WHEEL_GREEN;
    ctx.fill();
  }

  ctx.font =
    '20px system-ui, "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (let j = 0; j < SEGMENTS; j++) {
    const angleDeg = (360 / SEGMENTS) * j + (360 / SEGMENTS) / 2;
    const rad2 = ((angleDeg - 90) * Math.PI) / 180;
    const x = cx + r * WHEEL_ICON_RADIUS_FR * Math.cos(rad2);
    const y = cy + r * WHEEL_ICON_RADIUS_FR * Math.sin(rad2);
    const emoji = j % 2 === 0 ? '🍒' : '🍉';
    ctx.shadowColor = 'rgba(0,0,0,0.55)';
    ctx.shadowBlur = 3;
    ctx.shadowOffsetY = 1;
    ctx.fillText(emoji, x, y);
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
  }
  ctx.restore();
}

function drawWheelFrame(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  outerR: number
) {
  const grd = ctx.createLinearGradient(cx - outerR, cy - outerR, cx + outerR, cy + outerR);
  grd.addColorStop(0, '#4a3c28');
  grd.addColorStop(0.5, '#2a2218');
  grd.addColorStop(1, '#1a1410');
  ctx.beginPath();
  ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
  ctx.fillStyle = grd;
  ctx.fill();
  ctx.strokeStyle = '#8b6914';
  ctx.lineWidth = 8;
  ctx.stroke();
  ctx.strokeStyle = '#5c4309';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, outerR + 5, 0, Math.PI * 2);
  ctx.stroke();
}

/** 룰렛 중앙 금색 디스크(정보 카드 뒤 레이어) */
function drawCenterHubDisc(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  const hubR = 28;
  const g = ctx.createLinearGradient(cx - hubR, cy - hubR, cx + hubR, cy + hubR);
  g.addColorStop(0, '#f0b429');
  g.addColorStop(1, '#b8860b');
  ctx.beginPath();
  ctx.arc(cx, cy, hubR, 0, Math.PI * 2);
  ctx.fillStyle = g;
  ctx.fill();
  ctx.strokeStyle = '#8b6914';
  ctx.lineWidth = 3;
  ctx.stroke();
}

/** 룰렛 중심 — BLOCK / 타이머 세로 정렬 카드 (스핀 중에는 draw 호출 안 함) */
function drawWheelCenterInfoCard(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  m: CanvasTopModel
) {
  const padX = 16;
  const padY = 11;
  const lineGap = 5;
  const fontMain = '800 13px system-ui, "Malgun Gothic", "Apple SD Gothic Neo", sans-serif';
  const line1 = `BLOCK ${m.blockStr}`;
  const line2 = `타이머 ${m.timerStr}s`;

  ctx.save();
  ctx.font = fontMain;
  const w1 = ctx.measureText(line1).width;
  const w2 = ctx.measureText(line2).width;
  const tw = Math.max(w1, w2);
  const cardW = Math.max(Math.ceil(tw + padX * 2), 128);
  const lineH = 16;
  const cardH = padY + lineH + lineGap + lineH + padY;
  const rx = cx - cardW / 2;
  const ry = cy - cardH / 2;
  const cornerR = 14;

  ctx.shadowColor = 'rgba(0,0,0,0.5)';
  ctx.shadowBlur = 14;
  ctx.shadowOffsetY = 3;

  const barGrad = ctx.createLinearGradient(rx, ry, rx, ry + cardH);
  barGrad.addColorStop(0, '#352a22');
  barGrad.addColorStop(0.45, '#261f19');
  barGrad.addColorStop(1, '#1c1712');
  ctx.fillStyle = barGrad;
  roundRect(ctx, rx, ry, cardW, cardH, cornerR);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  ctx.save();
  roundRect(ctx, rx, ry, cardW, cardH, cornerR);
  ctx.clip();
  const gloss = ctx.createLinearGradient(rx, ry, rx, ry + cardH * 0.55);
  gloss.addColorStop(0, 'rgba(255, 220, 150, 0.12)');
  gloss.addColorStop(0.4, 'rgba(255, 220, 150, 0.03)');
  gloss.addColorStop(1, 'transparent');
  ctx.fillStyle = gloss;
  ctx.fillRect(rx, ry, cardW, cardH);
  ctx.restore();

  ctx.strokeStyle = '#a67c00';
  ctx.lineWidth = 2;
  roundRect(ctx, rx, ry, cardW, cardH, cornerR);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(255, 220, 150, 0.22)';
  ctx.lineWidth = 1;
  roundRect(ctx, rx + 1, ry + 1, cardW - 2, cardH - 2, Math.max(6, cornerR - 1));
  ctx.stroke();

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const y1 = ry + padY + lineH / 2;
  const y2 = y1 + lineH + lineGap;

  ctx.font = fontMain;
  ctx.fillStyle = '#d6cbb8';
  ctx.fillText(line1, cx, y1);
  ctx.fillStyle = '#f5e6a8';
  ctx.fillText(line2, cx, y2);

  ctx.restore();
}

/** 12시 방향 고정 포인터 — 판은 회전, 화살표는 월드 좌표에 고정 */
function drawWheelWinPointer(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  outerR: number
) {
  const tipY = cy - outerR + 22;
  const baseY = cy - outerR - 6;
  const halfW = 14;
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.45)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 1;
  const pg = ctx.createLinearGradient(cx, baseY, cx, tipY);
  pg.addColorStop(0, '#fff3a0');
  pg.addColorStop(0.45, '#f5cc33');
  pg.addColorStop(1, '#d4a017');
  ctx.beginPath();
  ctx.moveTo(cx, tipY);
  ctx.lineTo(cx - halfW, baseY);
  ctx.lineTo(cx + halfW, baseY);
  ctx.closePath();
  ctx.fillStyle = pg;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.strokeStyle = '#5c4309';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

export class Slot2FullViewCanvas {
  private canvas: HTMLCanvasElement;
  private bgImage: HTMLImageElement | null = null;
  private logoImage: HTMLImageElement | null = null;
  private invalidate?: () => void;
  private logicalH = 0;
  private dpr = 1;
  private ctx2d: CanvasRenderingContext2D | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const bump = () => this.invalidate?.();
    const img = new Image();
    img.src = bgSrc;
    img.onload = () => {
      this.bgImage = img;
      bump();
    };
    img.decode?.().catch(() => {});
    const logo = new Image();
    logo.src = logoSrc;
    logo.onload = () => {
      this.logoImage = logo;
      bump();
    };
    logo.decode?.().catch(() => {});
  }

  /** 배경·로고 로드 후 캔버스를 다시 그릴 때 호출 (예: flushDraw) */
  setInvalidate(cb: () => void) {
    this.invalidate = cb;
  }

  getTotalHeight(): number {
    return this.logicalH;
  }

  draw(m: CanvasTopModel): void {
    const parent = this.canvas.parentElement;
    const hostW =
      parent?.clientWidth ||
      globalThis.document?.documentElement?.clientWidth ||
      this.canvas.clientWidth ||
      360;
    const fullW = Math.max(280, Math.floor(hostW));
    const w = Math.min(MAX_W, fullW - 16);
    const offsetX = (fullW - w) / 2;
    const topInset = Math.max(0, Math.floor(m.topInsetPx));

    const bottomPad = 12;
    /** HTML 탭 행과 동일 세로 — 내역·검증 옆 로고 */
    const titleBandH = Math.max(40, Math.floor(m.logoRowH ?? 52));
    const gapAfterLogo = 10;
    const wheelOuter = 120;
    const vhCss =
      Math.floor(globalThis.innerHeight || globalThis.document?.documentElement?.clientHeight || 600);
    /** getBoundingClientRect 는 매 프레임 호출 시 레이아웃 스래싱 유발 → clientHeight 우선 */
    const parentH = parent
      ? Math.floor(parent.clientHeight || parent.getBoundingClientRect().height || 0)
      : 0;

    let y = topInset;

    const contentBottom = y + titleBandH + gapAfterLogo + wheelOuter * 2 + 16 + bottomPad;
    const rawH = Math.max(contentBottom, parentH, vhCss);
    const totalH = Math.ceil(rawH / 2) * 2;

    this.logicalH = totalH;
    this.dpr = Math.min(globalThis.devicePixelRatio || 1, 2.5);
    const bw = Math.max(1, Math.round(fullW * this.dpr));
    const bh = Math.max(1, Math.round(totalH * this.dpr));
    if (this.canvas.width !== bw || this.canvas.height !== bh) {
      this.canvas.width = bw;
      this.canvas.height = bh;
    }
    this.canvas.style.width = `${fullW}px`;
    this.canvas.style.height = `${totalH}px`;

    if (!this.ctx2d) {
      this.ctx2d = this.canvas.getContext('2d', {
        alpha: true,
        desynchronized: true,
      });
    }
    const ctx = this.ctx2d;
    if (!ctx) return;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    ctx.fillStyle = '#1e1b16';
    ctx.fillRect(0, 0, fullW, totalH);
    if (this.bgImage?.complete && this.bgImage.naturalWidth) {
      const iw = this.bgImage.naturalWidth;
      const ih = this.bgImage.naturalHeight;
      const sx = fullW / iw;
      const sy = totalH / ih;
      /** 전체 영역을 덮도록 항상 cover (여백 없음, 비율에 따라 가로/세로 일부 크롭) */
      const scale = Math.max(sx, sy);
      const dw = iw * scale;
      const dh = ih * scale;
      const dx = (fullW - dw) / 2;
      const dy = (totalH - dh) / 2;
      ctx.drawImage(this.bgImage, dx, dy, dw, dh);
      const g = ctx.createLinearGradient(0, 0, 0, totalH);
      g.addColorStop(0, 'rgba(42,37,32,0.72)');
      g.addColorStop(0.35, 'rgba(30,27,22,0.78)');
      g.addColorStop(0.65, 'rgba(30,27,22,0.82)');
      g.addColorStop(1, 'rgba(42,37,32,0.7)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, fullW, totalH);
    } else {
      const g2 = ctx.createLinearGradient(0, 0, 0, totalH);
      g2.addColorStop(0, '#2a2520');
      g2.addColorStop(0.4, '#1e1b16');
      g2.addColorStop(1, '#252119');
      ctx.fillStyle = g2;
      ctx.fillRect(0, 0, fullW, totalH);
    }

    ctx.save();
    ctx.translate(offsetX, 0);

    const hw = w - PAD_X * 2;
    const titleCx = w / 2;
    const titleCy = y + titleBandH / 2;
    const li = this.logoImage;
    /** 양옆 HTML 탭과 겹치지 않게 로고 가로 폭 제한 */
    const logoMaxW = Math.min(hw - 12, w * 0.5);
    if (li?.complete && li.naturalWidth) {
      const pad = 4;
      const maxW = Math.max(80, logoMaxW - pad * 2);
      const maxH = titleBandH - pad * 2;
      const lr = li.naturalWidth / li.naturalHeight;
      let dw = maxW;
      let dh = dw / lr;
      if (dh > maxH) {
        dh = maxH;
        dw = dh * lr;
      }
      ctx.drawImage(li, titleCx - dw / 2, titleCy - dh / 2, dw, dh);
    } else {
      ctx.fillStyle = '#f5e6a8';
      ctx.shadowColor = 'rgba(0,0,0,0.75)';
      ctx.shadowBlur = 6;
      ctx.shadowOffsetY = 2;
      ctx.font = '800 20px "Times New Roman", Georgia, serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('CANDLE ROULETTE', titleCx, titleCy);
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
    }
    y += titleBandH + gapAfterLogo;

    const cx = w / 2;
    const cy = y + wheelOuter;
    drawWheelFrame(ctx, cx, cy, wheelOuter);
    const innerR = wheelOuter - 14;
    drawWheelRotated(ctx, cx, cy, innerR, m.wheelAngleDeg);
    drawCenterHubDisc(ctx, cx, cy);
    if (!m.blockRowDimmed) {
      drawWheelCenterInfoCard(ctx, cx, cy, m);
    }

    drawWheelWinPointer(ctx, cx, cy, wheelOuter);

    ctx.restore();
  }
}

export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/** 룰렛 등: 시작·끝 모두 완만 (가속 후 감속) */
export function easeInOutQuint(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x < 0.5 ? 16 * x * x * x * x * x : 1 - Math.pow(-2 * x + 2, 5) / 2;
}
